// Track layouts for the two race modes, as centre-line points in metres-ish
// world units (x east, z south), driven in point order, closed loop.
//
//   grandPrix  inspired by a classic Ardennes circuit: pit straight, tight
//              right hairpin, the uphill left-right-left, a long straight,
//              a chicane, a downhill hairpin, the double-left, and one smooth
//              right-hander back onto the pit straight.
//   endurance  a long, narrow endurance circuit: a curve under the bridge
//              after the start, esses, a very long straight broken by two
//              chicanes, a right-angle corner at its end, a 90-degree corner
//              and a slow hairpin, fast S-bends on the way back, and a double
//              chicane before the pit straight.
//
// Each layout also names its pit straight: the pit lane runs parallel to it
// on `pitSide` (+1 = left of travel, -1 = right), with one garage per car.

export type Point = [number, number]

export type Layout = {
  id: 'grand_prix' | 'endurance'
  points: Point[]
  scale: number
  width: number
  // Pit lane: straight segment from `pitFrom` to `pitTo` (pre-scale), offset
  // `pitOffset` from the track centre, garages `garageOffset` further out.
  pitFrom: Point
  pitTo: Point
  // How far before the lane the pit entry leaves the track, and how far after
  // it the exit rejoins, as fractions of the lane length (both on the straight).
  pitEntryLead: number
  pitExitLead: number
  pitOffset: number
  garageOffset: number
  // Moves the pit building and its garages along the lane (metres, + toward pitTo).
  garageShift?: number
  // Where the start/finish line and the bridge sit, as a fraction of the lap.
  startU: number
  bridgeU?: number
  // Fractions of the lap for grandstands and floodlights.
  stands: number[]
  // Overhead gantries (the one at startU carries the start lights).
  gantries: number[]
  // Grandstands along the main straight, opposite the pits.
  straightStands: number
  lights: number[]
  // Infield dressing built in Blender (art/blender/circuit.py).
  lake?: boolean
  ferrisWheel?: boolean
}

export const grandPrix: Layout = {
  id: 'grand_prix',
  scale: 1.6,
  width: 12,
  points: [
    [0, 70], [0, 40], [0, 10], [0, -20], [0, -42],
    // tight right hairpin at the top of the pit straight
    [3, -54], [12, -61], [22, -59], [29, -50],
    // a long left that sweeps onto the back straight
    [31, -38], [36, -27], [45, -18], [57, -11], [72, -6], [90, -4],
    // the long straight
    [110, -6], [140, -15], [175, -30], [196, -41],
    // one fast right at its end
    [214, -46], [230, -43], [241, -33], [246, -19], [247, -5],
    // downhill hairpin
    [245, 8], [237, 15], [226, 17],
    [210, 24], [195, 36],
    // the fast double-left
    [184, 49], [180, 65], [186, 81],
    [195, 92], [199, 102], [199, 113],
    // one long sweeping right onto the blast back
    [200, 124], [198, 135], [192, 145], [183, 152], [171, 156],
    // the long blast back
    [158, 154], [130, 143], [100, 129], [74, 115],
    [52, 101], [36, 91],
    // one smooth right-hander onto the pit straight
    [22, 89], [11, 86.5], [4, 81], [0.8, 74],
  ],
  pitFrom: [0, 50],
  pitTo: [0, -30],
  pitEntryLead: 0.18,
  pitExitLead: 0.12,
  pitOffset: 14,
  garageOffset: 3.5,
  garageShift: -3.4,
  startU: 0.08,
  stands: [0.12, 0.2, 0.36, 0.5, 0.62, 0.74, 0.9],
  // Three big-banner gantries after the start, one at the start of each sector.
  gantries: [0.08, 0.36, 0.58, 0.8],
  straightStands: 3,
  lights: [0.12, 0.25, 0.45, 0.55, 0.7, 0.85, 0.95],
}

export const endurance: Layout = {
  id: 'endurance',
  scale: 2.0,
  width: 11,
  points: [
    // pit straight, heading north
    [0, 40], [0, 20], [0, 0], [0, -20],
    // the curve under the bridge
    [3, -32], [12, -41], [24, -44.5],
    // esses
    [36, -41], [48, -38], [57, -31],
    // right onto the long straight
    [63, -18], [66, 0], [69, 30], [72, 60], [73.5, 80],
    // first chicane: a gentle left-right
    [74.8, 92], [78.6, 101], [78.8, 108], [76.8, 117], [76.6, 130],
    [78, 150], [79.3, 166],
    // second chicane, the other way: a gentle right-left
    [80.6, 178], [77.2, 187], [77.0, 194], [79.4, 203], [81.6, 216],
    [84, 245], [85, 266],
    // the corner at the end of the straight, rounded
    [85.3, 278], [83.6, 287.5], [77.5, 291], [68, 290.5],
    [52, 286], [32, 272], [18, 252],
    // the 90-degree corner
    [12, 238], [6, 228.5], [-2, 223.5], [-12, 222],
    // slow hairpin
    [-21.5, 219.5], [-27, 211.5], [-28, 199],
    [-28, 172], [-26, 136],
    // fast S-bends, long and smooth
    [-23, 118], [-17, 104], [-14.5, 92], [-16.5, 81], [-13, 71],
    // double chicane before the pits, softened
    [-7, 62.5], [-2.5, 55], [0.2, 48],
  ],
  pitFrom: [0, 34],
  pitTo: [0, -12],
  pitEntryLead: 0.12,
  pitExitLead: 0.12,
  pitOffset: -13,
  garageOffset: -9,
  startU: 0.015,
  bridgeU: 0.05,
  stands: [0.06, 0.1, 0.42, 0.55, 0.66, 0.8, 0.9],
  // Three big-banner gantries after the start, one at the start of each sector.
  gantries: [0.015, 0.24, 0.4, 0.62],
  straightStands: 2,
  lights: [0.08, 0.15, 0.24, 0.33, 0.42, 0.5, 0.58, 0.66, 0.74, 0.82, 0.9, 0.96],
  lake: true,
  ferrisWheel: true,
}

export const layouts = { grand_prix: grandPrix, endurance }
